import { FC, useState } from "react";
import { DateTime } from "luxon";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";

import { durationFormatter, numFormatter } from "@/utils/helpers";
import { TopVideosResource } from "@/types/resources";
import {
  Td,
  Topics,
  TopicsList,
  Thumbnail,
  ActionsWrap,
} from "@/components/Table";
import Text from "@/components/Text";
import RestrictedContent from "@/components/RestrictedContent";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as Pencil } from "@/assets/icons/pencil-with-background.svg";
import TopicTag from "@/components/TopicTag";
import AttachmentModal from "@/pages/AssignTopics/components/AttachmentModal";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 192px;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const TopicsTd = styled(Td)`
  min-width: 200px;
`;

export interface AttachmentModalState {
  isOpen: boolean;
  category: string;
}

interface Props {
  video: TopVideosResource;
  videosQueryKey: any;
}

const TopTopicsInnerTableRow: FC<Props> = ({ video, videosQueryKey }) => {
  const [attachmentModalState, setAttachmentModalState] =
    useState<AttachmentModalState>({ isOpen: false, category: "" });

  const scriptTopics = video.topics?.filter(
    (topic) => topic.category === "script"
  );

  const thumbTopics = video.topics?.filter(
    (topic) => topic.category === "thumb"
  );

  const titleTopics = video.topics?.filter(
    (topic) => topic.category === "title"
  );

  const typeTopics = video.topics?.filter((topic) => topic.category === "type");

  return (
    <tr>
      <Td>
        <Thumbnail src={video.thumbnail} />
      </Td>
      <Td>
        <Text
          as="a"
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          $type="smallBold"
        >
          {video.title}
        </Text>
        <br />
        <Text
          href={video.channelUrl}
          as="a"
          target="_blank"
          $type="extraSmall"
          $color="gray"
          rel="noopener noreferrer"
        >
          {video.channelName}
        </Text>
      </Td>
      <Td>
        <NoWrap>{DateTime.fromISO(video.publishedAt).toRelative()}</NoWrap>
        <br />
        <Text $type="extraSmall" $color="gray" as="span">
          <NoWrap>
            {DateTime.fromISO(video.publishedAt).toLocaleString()}
          </NoWrap>
        </Text>
      </Td>
      <Td>
        <NoWrap>{video.views ? numFormatter(video.views) : "-"}</NoWrap>
      </Td>
      <Td>
        <NoWrap>
          {video.duration ? durationFormatter(video.duration) : "-"}
        </NoWrap>
      </Td>
      <Td>
        <NoWrap>{video.v7 ? numFormatter(video.v7) : "-"}</NoWrap>
      </Td>
      <Td>
        <NoWrap>{video.v30 ? numFormatter(video.v30) : "-"}</NoWrap>
      </Td>
      <Td>
        <NoWrap>{video.vph7 ? numFormatter(video.vph7) : "-"}</NoWrap>
      </Td>
      <Td>
        <NoWrap>{video.vph30 ? numFormatter(video.vph30) : "-"}</NoWrap>
      </Td>
      <Td>
        <NoWrap>{video.v7Index?.toFixed(2) ?? "-"}</NoWrap>
      </Td>
      <Td>
        <NoWrap>{video.v30Index?.toFixed(2) ?? "-"}</NoWrap>
      </Td>
      <Td>
        <RestrictedContent>
          <NoWrap>
            {video.revenue ? `$ ${numFormatter(video.revenue)}` : "-"}
          </NoWrap>
        </RestrictedContent>
      </Td>
      <Td>
        <RestrictedContent>
          <NoWrap>{video.rpm !== null ? `$ ${video.rpm}` : "-"}</NoWrap>
        </RestrictedContent>
      </Td>
      <Td>
        <RestrictedContent>
          <NoWrap>{video.r7 ? `$ ${numFormatter(video.r7)}` : "-"}</NoWrap>
        </RestrictedContent>
      </Td>
      <Td>
        <RestrictedContent>
          <NoWrap>{video.r30 ? `$ ${numFormatter(video.r30)}` : "-"}</NoWrap>
        </RestrictedContent>
      </Td>
      <Td>
        <RestrictedContent>
          <NoWrap>{video.r7Index?.toFixed(2) ?? "-"}</NoWrap>
        </RestrictedContent>
      </Td>
      <Td>
        <RestrictedContent>
          <NoWrap>{video.r30Index?.toFixed(2) ?? "-"}</NoWrap>
        </RestrictedContent>
      </Td>
      <TopicsTd>
        <Topics $hasTopics={!!scriptTopics?.length}>
          <Pencil />
          {!!scriptTopics?.length ? (
            <TopicsList>
              {scriptTopics.map((topic) => (
                <TopicTag topic={topic} key={topic.id} />
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "script",
                })
              }
            >
              Assign topics
            </Text>
          )}
        </Topics>
      </TopicsTd>
      <TopicsTd>
        <Topics $hasTopics={!!thumbTopics?.length}>
          <Pencil />
          {!!thumbTopics?.length ? (
            <TopicsList>
              {thumbTopics.map((topic) => (
                <TopicTag topic={topic} key={topic.id} />
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "thumb",
                })
              }
            >
              Assign topics
            </Text>
          )}
        </Topics>
      </TopicsTd>
      <TopicsTd>
        <Topics $hasTopics={!!titleTopics?.length}>
          <Pencil />
          {!!titleTopics?.length ? (
            <TopicsList>
              {titleTopics.map((topic) => (
                <TopicTag topic={topic} key={topic.id} />
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "title",
                })
              }
            >
              Assign topics
            </Text>
          )}
        </Topics>
      </TopicsTd>
      <TopicsTd>
        <Topics $hasTopics={!!typeTopics?.length}>
          <Pencil />
          {!!typeTopics?.length ? (
            <TopicsList>
              {typeTopics.map((type) => (
                <TopicTag topic={type} key={type.id} />
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "type",
                })
              }
            >
              Assign types
            </Text>
          )}
        </Topics>
        <AnimatePresence>
          {attachmentModalState.isOpen && (
            <AttachmentModal
              videosQueryKey={videosQueryKey}
              onClose={() =>
                setAttachmentModalState({ isOpen: false, category: "" })
              }
              video={video}
              attachmentModalState={attachmentModalState}
            />
          )}
        </AnimatePresence>
      </TopicsTd>
      <Td $actionsCol>
        <ActionsWrap>
          <StyledActionsDropdown>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "script",
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!scriptTopics?.length
                ? "Edit script topics"
                : "Assign script topics"}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "thumb",
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!thumbTopics?.length
                ? "Edit thumb topics"
                : "Assign thumb topics"}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "title",
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!thumbTopics?.length
                ? "Edit title topics"
                : "Assign title topics"}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: "type",
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!typeTopics?.length ? "Edit types" : "Assign types"}
            </ActionsDropdownButton>
          </StyledActionsDropdown>
          <AnimatePresence>
            {attachmentModalState.isOpen && (
              <AttachmentModal
                videosQueryKey={videosQueryKey}
                onClose={() =>
                  setAttachmentModalState({ isOpen: false, category: "" })
                }
                video={video}
                attachmentModalState={attachmentModalState}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default TopTopicsInnerTableRow;
